import { useEffect, useState } from 'react';
import { Save, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

/**
 * Shared profile form — self-service or admin editing another user.
 * @param {object} user — target user record
 * @param {object} directory — { designations, offices }
 * @param {'self'|'admin'} mode
 * @param {object|null} pendingDesignationChange
 * @param {function} onSaved — (result) => void
 */
export default function ProfileEditor({
    user,
    directory = { designations: [], offices: [] },
    mode = 'self',
    pendingDesignationChange = null,
    onSaved,
}) {
    const isAdmin = mode === 'admin';
    const [f, setF] = useState({
        name: user.name || '',
        phone: user.phone || '',
        territory: user.territory || '',
        designation: user.designation || '',
        salesDesignation: user.salesDesignation || '',
        officeBranch: user.officeBranch || '',
        zohoUserId: user.zohoUserId || '',
        reportsTo: user.reportsTo?._id || user.reportsTo || '',
    });
    const [bdmCandidates, setBdmCandidates] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setF({
            name: user.name || '',
            phone: user.phone || '',
            territory: user.territory || '',
            designation: user.designation || '',
            salesDesignation: user.salesDesignation || '',
            officeBranch: user.officeBranch || '',
            zohoUserId: user.zohoUserId || '',
            reportsTo: user.reportsTo?._id || user.reportsTo || '',
        });
    }, [user._id, user.updatedAt]);

    const salesRole = f.salesDesignation || user.salesDesignation || '';
    const showReportsTo = isAdmin || ['bda', 'bde'].includes(salesRole);
    /* BDM list is scoped to the BDA/BDE office — saved branch for self, form branch for admin. */
    const bdmOfficeBranch = isAdmin
        ? (f.officeBranch || user.officeBranch || '')
        : (user.officeBranch || '');

    useEffect(() => {
        if (!showReportsTo) return;
        if (!bdmOfficeBranch) {
            setBdmCandidates([]);
            return;
        }
        api.get('/users/picker', {
            params: { salesDesignation: 'bdm', officeBranch: bdmOfficeBranch },
        })
            .then(r => setBdmCandidates((r.data.items || []).filter(b => String(b._id) !== String(user._id))));
    }, [user._id, isAdmin, showReportsTo, bdmOfficeBranch]);

    /* Drop a stale BDM pick if their office no longer matches. */
    useEffect(() => {
        if (!f.reportsTo || bdmCandidates.length === 0) return;
        if (!bdmCandidates.some(b => String(b._id) === String(f.reportsTo))) {
            setF(prev => ({ ...prev, reportsTo: '' }));
        }
    }, [bdmCandidates, f.reportsTo]);

    const save = async () => {
        setBusy(true);
        try {
            const payload = { ...f };
            if (!payload.reportsTo) payload.reportsTo = null;
            const { data } = isAdmin
                ? await api.patch(`/users/${user._id}`, payload)
                : await api.patch('/users/me/profile', payload);

            if (data.designationChangeSubmitted) {
                toast.success('Personal details saved. Designation change sent for admin approval.');
            } else {
                toast.success('Profile saved');
            }
            onSaved?.(data);
        } catch {
            /* toasted */
        } finally {
            setBusy(false);
        }
    };

    const pending = pendingDesignationChange?.status === 'pending' ? pendingDesignationChange : null;

    return (
        <div className="space-y-4">
            {pending && !isAdmin && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <div className="font-semibold inline-flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Designation change pending approval
                    </div>
                    <div className="mt-2 text-xs space-y-1">
                        {pending.proposed?.designation && (
                            <div>Title: <b>{pending.proposed.designation}</b></div>
                        )}
                        {pending.proposed?.salesDesignation && (
                            <div>Role: <b>{pending.proposed.salesDesignation}</b></div>
                        )}
                        {pending.proposed?.officeBranch && (
                            <div>Office: <b>{pending.proposed.officeBranch}</b></div>
                        )}
                    </div>
                </div>
            )}

            {!isAdmin && (
                <p className="text-xs text-ink-500">
                    Changes to designation, sales role, or office branch require admin approval.
                    {showReportsTo ? ' Your reporting BDM saves immediately.' : ' Other personal fields update immediately.'}
                </p>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="ag-card p-5 space-y-3">
                    <h3 className="font-bold text-sm uppercase tracking-wide text-ink-500">Personal info</h3>
                    <Row label="Full name">
                        <input className="ag-input" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
                    </Row>
                    <Row label="Email">
                        <input className="ag-input bg-ink-50 text-ink-500" value={user.email} disabled />
                    </Row>
                    <Row label="Phone">
                        <input className="ag-input" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} placeholder="+91 …" />
                    </Row>
                    <Row label="Territory">
                        <input className="ag-input" value={f.territory} onChange={e => setF({ ...f, territory: e.target.value })} />
                    </Row>
                    <Row label="Free-form title">
                        <input className="ag-input" value={f.designation} onChange={e => setF({ ...f, designation: e.target.value })} placeholder="Senior BDM · Noida" />
                    </Row>
                </div>

                <div className="ag-card p-5 space-y-3">
                    <h3 className="font-bold text-sm uppercase tracking-wide text-ink-500">Job &amp; org</h3>
                    <Row label="Sales designation">
                        <select className="ag-input" value={f.salesDesignation} onChange={e => setF({ ...f, salesDesignation: e.target.value })}>
                            <option value="">— not set —</option>
                            {(directory.designations?.length > 0
                                ? directory.designations.map(d => (
                                    <option key={d.slug} value={d.slug}>{d.label}</option>
                                ))
                                : (
                                    <>
                                        <option value="bda">BDA</option>
                                        <option value="bde">BDE</option>
                                        <option value="bdm">BDM</option>
                                        <option value="bhead">BHead</option>
                                        <option value="operations">Operations</option>
                                        <option value="performance_marketing">Performance Marketing</option>
                                        <option value="other">Other</option>
                                    </>
                                ))}
                        </select>
                    </Row>
                    <Row label="Office branch">
                        {directory.offices?.length > 0 ? (
                            <select className="ag-input" value={f.officeBranch} onChange={e => setF({ ...f, officeBranch: e.target.value })}>
                                <option value="">— not set —</option>
                                {directory.offices.map(o => (
                                    <option key={o.slug} value={o.label}>{o.label}</option>
                                ))}
                            </select>
                        ) : (
                            <input className="ag-input" value={f.officeBranch} onChange={e => setF({ ...f, officeBranch: e.target.value })} />
                        )}
                    </Row>
                    {showReportsTo && (
                        <Row label="Reports to (BDM)">
                            <select
                                className="ag-input"
                                value={f.reportsTo || ''}
                                onChange={e => setF({ ...f, reportsTo: e.target.value })}
                                disabled={!bdmOfficeBranch || bdmCandidates.length === 0}
                            >
                                <option value="">
                                    {!bdmOfficeBranch
                                        ? '— set office branch first —'
                                        : bdmCandidates.length === 0
                                            ? `— no BDM in ${bdmOfficeBranch} —`
                                            : '— pick your BDM —'}
                                </option>
                                {bdmCandidates.map(b => (
                                    <option key={b._id} value={b._id}>{b.name}</option>
                                ))}
                            </select>
                            {bdmOfficeBranch && bdmCandidates.length === 0 && (
                                <p className="text-[11px] text-amber-700 mt-1">
                                    No BDM found for <b>{bdmOfficeBranch}</b>. Ask admin to add one for this office.
                                </p>
                            )}
                            {!bdmOfficeBranch && (
                                <p className="text-[11px] text-ink-500 mt-1">
                                    Your office branch must be set before you can pick a BDM.
                                </p>
                            )}
                            {!isAdmin && user.reportsTo?.name && !f.reportsTo && bdmOfficeBranch && (
                                <p className="text-[11px] text-ink-500 mt-1">
                                    Currently using office default ({user.reportsTo.name}).
                                </p>
                            )}
                        </Row>
                    )}
                    <Row label="Zoho user ID">
                        <input
                            className="ag-input font-mono text-xs"
                            value={f.zohoUserId}
                            onChange={e => setF({ ...f, zohoUserId: e.target.value })}
                            placeholder="Optional"
                        />
                    </Row>
                    <div className="text-[11px] text-ink-500">
                        Role: <span className="font-semibold text-ink-700">{user.role}</span>
                        {isAdmin ? ' — change role from Users list.' : ''}
                    </div>
                </div>
            </div>

            <div className="flex justify-end">
                <button type="button" className="ag-btn-primary" disabled={busy} onClick={save}>
                    <Save className="h-4 w-4" /> {busy ? 'Saving…' : 'Save profile'}
                </button>
            </div>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div>
            <label className="ag-label">{label}</label>
            {children}
        </div>
    );
}
